// ============================================================
// errorMessages.js
// Backend error reasons → admin-facing messages.
//
// The backend uses short snake_case reason codes (e.g.
// 'tenant_not_found') in the ApiError.message field. The UI should
// never display the raw code — translate via this dictionary so
// admins see "Tenant not found" instead.
//
// Unknown reasons fall back to the generic message. Add entries
// here as new backend reasons surface; don't extend per-page.
// ============================================================

const ERROR_MESSAGES = {
  // Generic
  invalid_pagination: 'The page or page size is invalid.',
  page_size_exceeded: 'Page size cannot be greater than 200.',
  invalid_search:     'The search term is too long.',
  invalid_role_key:   'Invalid role.',
  invalid_action:     'Invalid action filter.',
  invalid_entity_type: 'Invalid entity type filter.',
  invalid_entity_id:  'Invalid entity ID filter.',
  entityId_requires_entityType: 'Entity ID filter requires an entity type filter.',
  invalid_client_id:  'Invalid client ID.',
  invalid_client_user_id: 'Invalid user ID.',
  invalid_module_key: 'Invalid module key.',
  invalid_body:       'The request data is invalid.',
  invalid_tenant_id:  'Invalid tenant ID.',
  invalid_start_date: 'Invalid start date.',
  invalid_end_date:   'Invalid end date.',
  invalid_group_by:   'Invalid group-by value.',
  end_before_start:   'End date must be after the start date.',

  // Tenants
  tenant_not_found:   'Tenant not found or inactive.',

  // Clients
  client_not_found:   'Client not found.',

  // Users
  user_not_found:     'User not found.',
  user_already_inactive: 'This user is already inactive.',
  user_already_active:   'This user is already active.',
  user_role_does_not_support_per_agent_grants:
    'Per-agent entitlements only apply to users with the Admin role.',
  user_role_does_not_support_access_expiry:
    'Access expiry only applies to users with the Auditor role.',
  role_unchanged: 'The user already has that role.',
  role_federation_managed:
    "This user's role comes from their organisation's group membership. Change the group mapping on the tenant's Federation tab instead.",
  cannot_demote_last_manager:
    'This is the only active manager on the client. Promote another user to manager first.',

  // Entitlements
  entitlement_already_exists:
    'This user already has this module granted.',
  entitlement_not_found:
    'No active grant exists for this module.',
  module_not_found_or_inactive:
    'That module does not exist or is currently disabled.',
  new_expiry_out_of_range:
    'New expiry must be in the future and within 90 days from now.',

  // Invitations (existing)
  admin_requires_at_least_one_agent:
    'Admin invitations must include at least one module.',
  admin_must_not_have_access_duration:
    'Admin invitations cannot have an access duration.',
  auditor_requires_access_duration:
    'Auditor invitations must include an access duration.',
  auditor_must_not_have_agent_entitlements:
    'Auditor invitations cannot include modules.',
  manager_must_not_have_access_duration_or_agents:
    'Manager invitations cannot have an access duration or modules.',
  carer_must_not_have_access_duration_or_agents:
    'Carer invitations cannot have an access duration or modules.',
  invalid_agent_entitlement_module_id:
    'One of the selected modules is invalid.',
  access_duration_out_of_range:
    'Access duration must be between 1 and 90 days.',

  // Group mappings
  group_mapping_not_found:
    'This mapping may have already been removed. Refresh to verify.',

  // Audit template builder (versions / structure / publish)
  not_latest_version:
    'A newer version of this template already exists. Create the draft from the latest version instead.',
  draft_already_exists:
    'A draft version of this template already exists. Open it in the builder instead of creating another.',
  template_in_use:
    'Audits have been run against this version, so its structure cannot change. Create a new version instead.',
  already_active:
    'This version is already the active one.',
  template_has_no_sections:
    'This version has no sections yet. Add content in the builder before publishing.',
  use_publish:
    'Activate templates through Publish - publishing retires the previous version and moves schedules across atomically.',
};

/**
 * Translate a backend reason or ApiError to an admin-facing message.
 * Falls back to a generic message; never returns null/undefined.
 *
 * Resolution order (2026-07-13 — the Modules-tab 409 fix, applied as
 * a CLASS fix, not a one-off):
 *   1. A curated ERROR_MESSAGES entry for the reason code (unchanged
 *      behaviour for every mapped code).
 *   2. The backend's own human-readable `detail` string, when the
 *      error is an informative 4xx (our jsonResponse convention puts
 *      operator-safe plain English in `detail` — e.g. the module-
 *      toggle 409 module_required_by_dependent names the dependent
 *      module). Previously this was silently discarded and every
 *      unmapped-but-informative error collapsed to the generic
 *      string.
 *   3. The generic fallback — kept for genuinely unexpected failures
 *      (network errors, 5xx, no useful body). Raw codes/stacks are
 *      never shown.
 *
 * @param {string|Error|null|undefined} reasonOrError
 *   A reason code string, an ApiError, or null/undefined.
 * @returns {string}
 */
export function translateError(reasonOrError) {
  if (!reasonOrError) return 'Something went wrong. Please try again.';

  let reason;
  let apiError = null;
  if (typeof reasonOrError === 'string') {
    reason = reasonOrError;
  } else if (typeof reasonOrError === 'object' && reasonOrError.message) {
    reason = reasonOrError.message;
    apiError = reasonOrError;
  } else {
    return 'Something went wrong. Please try again.';
  }

  const curated = ERROR_MESSAGES[reason];
  if (curated) return curated;

  // Surface the backend's human-readable detail for informative 4xx
  // errors. Guards: never for network failures (no real body) and
  // never for 5xx (a 500's detail is not operator-actionable).
  //
  // The `hint` rides along when present (2026-08-28 — the shared-
  // calendar card follow-on): a detail says WHAT is wrong ("consent
  // missing"), the hint says where to go and fix it (Part B4 of the
  // setup runbook). Showing the first without the second is what left
  // an operator staring at a correct diagnosis with no next step. The
  // hint deliberately does NOT rescue a 5xx past the guard above — a
  // 5xx's next step is telemetry, not a toast.
  if (
    apiError &&
    typeof apiError.detail === 'string' &&
    apiError.detail.trim().length > 0 &&
    apiError.isNetworkError !== true &&
    typeof apiError.status === 'number' &&
    apiError.status >= 400 && apiError.status < 500
  ) {
    const hint = typeof apiError.hint === 'string' ? apiError.hint.trim() : '';
    return hint ? `${apiError.detail} ${hint}` : apiError.detail;
  }

  return 'Something went wrong. Please try again.';
}

export default ERROR_MESSAGES;
